import { useCallback } from 'react'
import { useMutation, useQuery } from 'urql'

import { useAuth } from '@app/context/auth'
import {
  GetUserQualifyingCertificateCountQuery,
  GetUserQualifyingCertificateCountQueryVariables,
  InsertSubmissionOfKnowledgeHubCertificateSplashScreenMutation,
  InsertSubmissionOfKnowledgeHubCertificateSplashScreenMutationVariables,
  Splash_Screens_Enum,
} from '@app/generated/graphql'
import { GET_USER_QUALIFYING_CERTIFICATE_COUNT } from '@app/queries/course_certificate/get-user-qualifying-certificate-count'
import { INSERT_SUBMISSION_OF_KNOWLEDGE_HUB_CERTIFICATE_SPLASH_SCREEN } from '@app/queries/submission_of_splash_screens/insert-submission-of-knowledge-hub-certificate-splash-screen'
import { RoleName } from '@app/types'

export const useProfileKnowledgeHubCertificateSplashScreen = () => {
  const { activeRole, profile } = useAuth()

  const isUser = Boolean(profile?.id && activeRole === RoleName.USER)

  const [{ data }] = useQuery<
    GetUserQualifyingCertificateCountQuery,
    GetUserQualifyingCertificateCountQueryVariables
  >({
    query: GET_USER_QUALIFYING_CERTIFICATE_COUNT,
    variables: { profileId: profile?.id ?? '' },
    pause: !isUser,
  })

  const qualifyingCertificateCount =
    data?.course_certificate_aggregate.aggregate?.count ?? 0

  // Count already acknowledged; backfilled for existing users so historical
  // certificates never trigger the pop-up.
  const acknowledgedCertificateCount =
    profile?.splashScreens?.find(
      screen =>
        screen.splash_screen === Splash_Screens_Enum.KnowledgeHubCertificate,
    )?.seen_count ?? 0

  // A new qualifying certificate exists since the last acknowledgement; one
  // pop-up covers any number at once.
  const isKnowledgeHubCertificateSplashScreenEnabled =
    isUser && qualifyingCertificateCount > acknowledgedCertificateCount

  const [, insertSubmissionOfSplashScreen] = useMutation<
    InsertSubmissionOfKnowledgeHubCertificateSplashScreenMutation,
    InsertSubmissionOfKnowledgeHubCertificateSplashScreenMutationVariables
  >(INSERT_SUBMISSION_OF_KNOWLEDGE_HUB_CERTIFICATE_SPLASH_SCREEN)

  const insertSubmissionOfKnowledgeHubCertificateSplashScreen =
    useCallback(async () => {
      if (!profile?.id) return

      return insertSubmissionOfSplashScreen({
        profileId: profile.id,
        seenCount: qualifyingCertificateCount,
      })
    }, [
      insertSubmissionOfSplashScreen,
      profile?.id,
      qualifyingCertificateCount,
    ])

  return {
    isKnowledgeHubCertificateSplashScreenEnabled,
    insertSubmissionOfKnowledgeHubCertificateSplashScreen,
  }
}
